// T8-BOARD-NUMBERS (universal-task plan §(c) wave 3): a board's "Numbers" tab, beside Files.
// What it shows comes from `GET boards/{id}/numbers`:
//   - four counts (open, done, overdue, due this week), tasks per status and tasks done per week;
//   - each number of the board (a task metric counted from its tasks) as a tile: the current
//     period, the change from the one before and a small trend;
//   - numbers a DE proposed, waiting for an admin.
// "Add number" (a modal, for whoever edits the board) picks what to count with the KPI builder,
// the board fixed. Remove is a hover icon on a tile no seat carries. Generic: nothing here knows
// what the board's tasks are.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  InlineBar,
  Input,
  NativeSelect,
  NativeSelectOption,
  Sparkline,
  StatTile,
} from "@companyos/ui";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlarmClockIcon,
  CalendarClockIcon,
  CircleCheckIcon,
  CircleDotIcon,
  HashIcon,
  PlusIcon,
  Trash2Icon,
  TrendingDownIcon,
  TrendingUpIcon,
} from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import { formatCardMetric } from "../format";
import {
  formOfTaskQuery,
  TaskQueryBuilder,
  type TaskQueryForm,
  taskQueryOfForm,
} from "../kpi/task-query";
import { problemText } from "../settings/problem-text";
import { workKeys } from "./work-queries";

export type BoardNumbers = OperationOutput<"boards.numbers">;
type Tile = BoardNumbers["tiles"][number];

export const numbersKey = (co: string, board: string) =>
  [...workKeys.board(co, board), "numbers"] as const;

export const boardNumbersQuery = (api: Api, co: string, board: string) =>
  queryOptions({
    queryKey: numbersKey(co, board),
    queryFn: () => api.client.boards.numbers({ params: { co, id: board } }),
    // Under the board's work key (a task write refreshes it); the snapshot job runs every 15
    // minutes, so an open tab rereads every 5.
    refetchInterval: 5 * 60_000,
  });

const CADENCE_WORD: Record<Tile["cadence"], string> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  yearly: "year",
};

/** The change from the period before: its sign, and whether it is the good direction. */
export function changeOf(t: Pick<Tile, "current" | "previous" | "direction">) {
  if (t.current === null || t.previous === null) return null;
  const delta = Math.round((t.current - t.previous) * 100) / 100;
  // `eq` / `between` say nothing of a direction: the change stays neutral.
  if (delta === 0 || t.direction === "eq" || t.direction === "between")
    return { delta, good: null as boolean | null };
  const lowerIsBetter = t.direction === "lte" || t.direction === "lt";
  return { delta, good: delta > 0 !== lowerIsBetter };
}

export function BoardNumbersTab({
  api,
  co,
  boardId,
  boardName,
}: {
  api: Api;
  co: string;
  boardId: string;
  boardName: string;
}) {
  const qc = useQueryClient();
  const q = useQuery(boardNumbersQuery(api, co, boardId));
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const n = q.data;
  if (q.isPending) return <p className="p-4 text-sm text-muted-foreground">Loading…</p>;
  if (q.isError || !n)
    return (
      <p role="alert" className="p-4 text-sm text-danger-text">
        {problemText(q.error, "The board's numbers could not be loaded.")}
      </p>
    );
  const remove = async (t: Tile) => {
    setError(null);
    try {
      const next = await api.client.boards.removeNumber({
        params: { co, id: boardId, metric_id: t.metric_def_id },
      });
      qc.setQueryData(numbersKey(co, boardId), next);
    } catch (e) {
      setError(problemText(e, `${t.name} could not be removed.`));
    }
  };
  const maxStatus = Math.max(1, ...n.by_status.map((s) => s.count));
  const doneWeeks = n.throughput.map((w) => w.done);
  return (
    <div data-slot="board-numbers" className="flex flex-col gap-6 py-4">
      <section aria-label="Counts" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile size="sm" label="Open" value={n.counts.open} icon={CircleDotIcon} />
        <StatTile size="sm" label="Done" value={n.counts.done} icon={CircleCheckIcon} />
        <StatTile
          size="sm"
          label="Overdue"
          value={
            <span className={n.counts.overdue > 0 ? "text-danger-text" : undefined}>
              {n.counts.overdue}
            </span>
          }
          icon={AlarmClockIcon}
        />
        <StatTile
          size="sm"
          label="Due this week"
          value={n.counts.due_this_week}
          icon={CalendarClockIcon}
        />
      </section>

      <section aria-labelledby="numbers-tiles" className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 id="numbers-tiles" className="text-sm font-semibold">
            Numbers
          </h2>
          <InfoTip label="About numbers">
            Counted from this board's tasks every 15 minutes. A DE can propose one too; an admin
            confirms it and it becomes a KPI on the board owner's seat.
          </InfoTip>
          {n.can_add ? (
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setAdding(true)}>
              <PlusIcon aria-hidden="true" />
              Add number
            </Button>
          ) : null}
        </div>
        {error ? (
          <p role="alert" className="text-xs text-danger-text">
            {error}
          </p>
        ) : null}
        {n.tiles.length === 0 ? (
          <EmptyState
            variant="compact"
            icon={HashIcon}
            title="No numbers yet"
            description={
              n.can_add
                ? "Add one: a count, a sum or an average of this board's tasks."
                : "Someone who edits the board can add one."
            }
          />
        ) : (
          <ul
            aria-label="Numbers"
            className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 xl:grid-cols-3"
          >
            {n.tiles.map((t) => (
              <NumberTile key={t.metric_def_id} tile={t} onRemove={() => void remove(t)} />
            ))}
          </ul>
        )}
        {n.proposals.length > 0 ? (
          <ul aria-label="Proposed numbers" className="m-0 flex list-none flex-col gap-1 p-0">
            {n.proposals.map((p) => (
              <li
                key={p.approval_id}
                className="flex items-center gap-2 text-xs text-muted-foreground"
              >
                <Badge variant="outline">DE</Badge>
                <span className="truncate">
                  <span className="text-foreground">{p.name}</span>
                  {p.proposed_by ? ` · proposed by ${p.proposed_by}` : ""} · waiting for an admin
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section aria-labelledby="numbers-status" className="flex flex-col gap-2">
          <h2 id="numbers-status" className="text-sm font-semibold">
            By status
          </h2>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {n.by_status.map((s) => (
              <li key={s.key} className="grid grid-cols-[8rem_1fr_2.5rem] items-center gap-3">
                <span className="truncate text-sm">{s.name}</span>
                <InlineBar
                  value={(s.count / maxStatus) * 100}
                  tone={s.done ? "success" : "primary"}
                  aria-hidden="true"
                />
                <span className="text-right text-sm tabular-nums">{s.count}</span>
              </li>
            ))}
          </ul>
        </section>
        <section aria-labelledby="numbers-throughput" className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <h2 id="numbers-throughput" className="text-sm font-semibold">
              Done per week
            </h2>
            <InfoTip label="About done per week">
              Tasks finished each week, the last 8 weeks.
            </InfoTip>
          </div>
          <div className="flex items-end gap-4">
            <Sparkline
              className="h-10 w-56"
              values={doneWeeks}
              label={`Done per week, last 8 weeks: ${doneWeeks.join(", ")}`}
            />
            <span className="text-sm tabular-nums text-muted-foreground">
              {doneWeeks.at(-1) ?? 0} this week
            </span>
          </div>
        </section>
      </div>

      {adding ? (
        <AddNumberDialog
          api={api}
          co={co}
          boardId={boardId}
          boardName={boardName}
          onClose={() => setAdding(false)}
          onSaved={(next) => qc.setQueryData(numbersKey(co, boardId), next)}
        />
      ) : null}
    </div>
  );
}

function NumberTile({ tile: t, onRemove }: { tile: Tile; onRemove: () => void }) {
  const change = changeOf(t);
  const values = t.trend.map((p) => p.value);
  const period = CADENCE_WORD[t.cadence];
  return (
    <li
      data-slot="number-tile"
      className="group/tile relative flex min-w-0 flex-col gap-1 rounded-lg border border-border bg-card px-4 py-3"
    >
      <div className="flex min-h-5 items-center gap-1">
        <span className="truncate text-xs text-muted-foreground">{t.name}</span>
        <InfoTip label={`How ${t.name} is counted`}>
          {t.how}
          {t.seat_count > 0 ? " It is a KPI on a seat." : ""}
          {t.computed_at === null ? " First count pending: shown live." : ""}
        </InfoTip>
        {t.can_remove ? (
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label={`Remove ${t.name}`}
            title="Remove"
            className="ml-auto opacity-0 group-hover/tile:opacity-100 group-focus-within/tile:opacity-100 pointer-coarse:opacity-100"
            onClick={onRemove}
          >
            <Trash2Icon aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      <div className="flex items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className="font-heading text-xl leading-7 font-semibold tabular-nums">
            {formatCardMetric(t.current, t.unit)}
          </span>
          <span className="text-xs text-muted-foreground">
            this {period}
            {change && change.delta !== 0 ? (
              <span
                className={cn(
                  "ml-1 inline-flex items-center gap-0.5 tabular-nums",
                  change.good === null
                    ? "text-muted-foreground"
                    : change.good
                      ? "text-success-text"
                      : "text-danger-text",
                )}
              >
                {change.delta > 0 ? (
                  <TrendingUpIcon className="size-3" aria-hidden="true" />
                ) : (
                  <TrendingDownIcon className="size-3" aria-hidden="true" />
                )}
                {change.delta > 0 ? "+" : ""}
                {formatCardMetric(change.delta, t.unit)} vs last {period}
              </span>
            ) : change ? (
              <span className="ml-1">· same as last {period}</span>
            ) : null}
          </span>
        </div>
        <Sparkline
          className="h-8 w-24 shrink-0"
          values={values}
          label={`${t.name}, last ${values.length} ${period}s: ${values
            .map((v) => (v === null ? "none" : String(v)))
            .join(", ")}`}
        />
      </div>
    </li>
  );
}

function AddNumberDialog({
  api,
  co,
  boardId,
  boardName,
  onClose,
  onSaved,
}: {
  api: Api;
  co: string;
  boardId: string;
  boardName: string;
  onClose: () => void;
  onSaved: (n: BoardNumbers) => void;
}) {
  const ids = { name: useId(), cadence: useId(), better: useId(), unit: useId() };
  const [name, setName] = useState("");
  const [query, setQuery] = useState<TaskQueryForm>(() => ({
    ...formOfTaskQuery(null),
    board_id: boardId,
    only: "done",
  }));
  const [cadence, setCadence] = useState<Tile["cadence"]>("weekly");
  const [unit, setUnit] = useState<Tile["unit"]>("number");
  const [better, setBetter] = useState<"gte" | "lte">("gte");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const built = taskQueryOfForm(query);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if ("error" in built) return setError(built.error);
    setPending(true);
    setError(null);
    try {
      const next = await api.client.boards.addNumber({
        params: { co, id: boardId },
        body: { name: name.trim(), task_query: built.query, cadence, unit, direction: better },
      });
      onSaved(next);
      onClose();
    } catch (err) {
      setError(problemText(err, "The number could not be added."));
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <form className="grid gap-4" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Add a number to {boardName}</DialogTitle>
            <DialogDescription>Counted from this board's tasks.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
            <Input
              id={ids.name}
              value={name}
              maxLength={120}
              placeholder="e.g. Done this week"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <TaskQueryBuilder api={api} co={co} value={query} onChange={setQuery} fixedBoard />
          <div className="grid gap-3 sm:grid-cols-3">
            <Field>
              <FieldLabel htmlFor={ids.cadence}>Per</FieldLabel>
              <NativeSelect
                id={ids.cadence}
                value={cadence}
                onChange={(e) => setCadence(e.target.value as Tile["cadence"])}
              >
                <NativeSelectOption value="daily">Day</NativeSelectOption>
                <NativeSelectOption value="weekly">Week</NativeSelectOption>
                <NativeSelectOption value="monthly">Month</NativeSelectOption>
                <NativeSelectOption value="quarterly">Quarter</NativeSelectOption>
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.unit}>Shown as</FieldLabel>
              <NativeSelect
                id={ids.unit}
                value={unit}
                onChange={(e) => setUnit(e.target.value as Tile["unit"])}
              >
                <NativeSelectOption value="number">Number</NativeSelectOption>
                <NativeSelectOption value="currency">Money</NativeSelectOption>
                <NativeSelectOption value="percent">Percent</NativeSelectOption>
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.better}>Better when</FieldLabel>
              <NativeSelect
                id={ids.better}
                value={better}
                onChange={(e) => setBetter(e.target.value as "gte" | "lte")}
              >
                <NativeSelectOption value="gte">Higher</NativeSelectOption>
                <NativeSelectOption value="lte">Lower</NativeSelectOption>
              </NativeSelect>
            </Field>
          </div>
          {error ? (
            <p role="alert" className="text-xs text-danger-text">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending || !name.trim() || "error" in built}>
              Add number
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
